import { Box, Button, TextField } from "@mui/material";
import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { createPermission, updatePermission } from "../../api/services/api";

interface PermissionFormProps {
  formData?: any;
  toggleDrawer: (open: boolean) => void;
}

export const PermissionForm = ({
  formData,
  toggleDrawer,
}: PermissionFormProps) => {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [resource, setResource] = useState("");
  const [action, setAction] = useState("");

  useEffect(() => {
    if (formData) {
      setName(formData.name || "");
      setDescription(formData.description || "");
      setResource(formData.resource || "");
      setAction(formData.action || "");
    }
  }, [formData]);

  const { mutate: createPermissionMutate, isLoading: isCreating } = useMutation(
    createPermission,
    {
      onSuccess: (res: any) => {
        queryClient.invalidateQueries("permissions");
        toast.success(res?.data?.message || "Permission created successfully");
        toggleDrawer(false);
        resetForm();
      },
      onError: (res: any) => {
        toast.error(
          res?.response?.data?.message || "Failed to create permission"
        );
      },
    }
  );

  const { mutate: updatePermissionMutate, isLoading: isUpdating } = useMutation(
    updatePermission,
    {
      onSuccess: (res: any) => {
        queryClient.invalidateQueries("permissions");
        toast.success(res?.data?.message || "Permission updated successfully");
        toggleDrawer(false);
        resetForm();
      },
      onError: (res: any) => {
        toast.error(
          res?.response?.data?.message || "Failed to update permission"
        );
      },
    }
  );

  const resetForm = () => {
    setName("");
    setDescription("");
    setResource("");
    setAction("");
  };

  const onSubmit = () => {
    const payload = {
      name,
      description,
      resource,
      action,
    };

    if (formData) {
      updatePermissionMutate({ body: payload, id: formData.id });
    } else {
      createPermissionMutate(payload);
    }
  };

  const isLoading = isCreating || isUpdating;

  return (
    <Box width="450px">
      <TextField
        fullWidth
        label="Permission Name"
        variant="standard"
        value={name}
        onChange={(e) => setName(e.target.value)}
        required
      />
      <TextField
        sx={{ mt: 2 }}
        fullWidth
        label="Description"
        variant="standard"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        multiline
        rows={3}
      />
      <TextField
        sx={{ mt: 2 }}
        fullWidth
        label="Resource"
        variant="standard"
        value={resource}
        onChange={(e) => setResource(e.target.value)}
        placeholder="e.g., users, patients, sessions"
        required
      />
      <TextField
        sx={{ mt: 2, mb: 3 }}
        fullWidth
        label="Action"
        variant="standard"
        value={action}
        onChange={(e) => setAction(e.target.value)}
        placeholder="e.g., create, read, update, delete"
        required
      />

      <Button
        type="submit"
        onClick={onSubmit}
        variant="contained"
        fullWidth
        disabled={isLoading || !name || !resource || !action}
      >
        {isLoading ? "Saving..." : formData ? "Update" : "Create"}
      </Button>
    </Box>
  );
};
